import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, Result, Spin } from "antd";
import { bookFromPage, getBookingPage, getBookingPageSlots } from "./calendarService";
import WeekSlots from "./WeekSlots";
import styles from "./style.module.scss";

function whenLabel(iso, tz, locale) {
  return new Intl.DateTimeFormat(locale, { timeZone: tz, weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
}

const stamp = (iso) => new Date(iso).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

/** Customers who gave only a phone number get no email, so their own calendar does the reminding. */
const AddToCalendar = ({ booking, business, t }) => {
  const title = `${booking.service_name}${business ? ` · ${business}` : ""}`;
  const details = booking.manage_url ? `${t("sonic_calendar.public_manage_hint")} ${booking.manage_url}` : "";
  const google =
    "https://calendar.google.com/calendar/render?action=TEMPLATE" +
    `&text=${encodeURIComponent(title)}&dates=${stamp(booking.start_at)}/${stamp(booking.end_at)}` +
    `&details=${encodeURIComponent(details)}&location=${encodeURIComponent(booking.location || "")}`;
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Pivony//Sonic Calendar//EN",
    "BEGIN:VEVENT",
    `UID:${stamp(booking.start_at)}-${Math.random().toString(36).slice(2)}@pivony.com`,
    `DTSTAMP:${stamp(new Date().toISOString())}`,
    `DTSTART:${stamp(booking.start_at)}`,
    `DTEND:${stamp(booking.end_at)}`,
    `SUMMARY:${title.replace(/[,;]/g, " ")}`,
    booking.location ? `LOCATION:${booking.location.replace(/[,;]/g, " ")}` : null,
    details ? `DESCRIPTION:${details.replace(/[,;]/g, " ")}` : null,
    "BEGIN:VALARM",
    "TRIGGER:-PT2H",
    "ACTION:DISPLAY",
    `DESCRIPTION:${title.replace(/[,;]/g, " ")}`,
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
  return (
    <p className={styles.addToCalendar}>
      <a className={styles.addToCalendarBtn} href={google} target="_blank" rel="noopener noreferrer">
        {t("sonic_calendar.public_add_google")}
      </a>
      <a className={styles.addToCalendarBtn} href={`data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`} download="appointment.ics">
        {t("sonic_calendar.public_add_other")}
      </a>
    </p>
  );
};

/** Public booking page: app.pivony.com/book/<slug>. Free; never a Qualified Prospect. */
const PublicBooking = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "en";
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);
  const [serviceId, setServiceId] = useState(null);
  // A person from the business's staff; null = anyone free.
  const [staffId, setStaffId] = useState(null);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    getBookingPage(slug)
      .then((res) => {
        setPage(res);
        if (res.services?.length === 1) setServiceId(res.services[0].id);
        document.title = `${res.business_name} · ${t("sonic_calendar.public_book_title")}`;
      })
      .catch(() => setMissing(true));
  }, [slug, t]);

  const load = useCallback(
    ({ from, days }) => getBookingPageSlots(slug, { serviceId, staffId, from, days, locale }),
    [slug, serviceId, staffId, locale]
  );

  const phoneOk = form.phone.replace(/\D/g, "").length >= 7;
  const emailOk = form.email ? /.+@.+\..+/.test(form.email) : !page?.require_email;
  const canSend = picked && form.name.trim().length >= 2 && phoneOk && emailOk;

  const submit = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await bookFromPage(slug, { service_id: serviceId, start: picked, ...form, locale, ...(staffId ? { staff_id: staffId } : {}) });
      setDone(res.booking);
    } catch (err) {
      if (err.code === "slot_taken") {
        setError(t("sonic_calendar.public_slot_taken"));
        setPicked(null);
        setReloadKey((k) => k + 1);
      } else {
        setError(t("sonic_calendar.public_error"));
      }
    } finally {
      setSending(false);
    }
  };

  if (missing) {
    return (
      <div className={styles.publicPage}>
        <Result status="404" title={t("sonic_calendar.public_not_found")} />
      </div>
    );
  }
  if (!page) {
    return (
      <div className={styles.publicPage}>
        <Spin />
      </div>
    );
  }
  const service = page.services.find((s) => s.id === serviceId);
  const staffChoices = (page.staff || []).filter((m) => !m.service_ids || m.service_ids.includes(serviceId));

  return (
    <div className={styles.publicPage}>
      <main className={styles.publicCard}>
        <h1>{page.business_name}</h1>
        <p className={styles.publicSub}>{t("sonic_calendar.public_book_title")}</p>

        {done ? (
          <div className={styles.done} role="status">
            <h2>{t("sonic_calendar.public_booked")}</h2>
            <p>
              {done.service_name}
              {done.staff_name ? ` · ${done.staff_name}` : ""} · {whenLabel(done.start_at, done.timezone, locale)}
              {done.location ? ` · ${done.location}` : ""}
            </p>
            <AddToCalendar booking={done} business={page.business_name} t={t} />
            {done.manage_url ? (
              <p>
                {t("sonic_calendar.public_manage_hint")} <a href={done.manage_url}>{done.manage_url}</a>
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {page.services.length > 1 ? (
              <section className={styles.publicStep}>
                <h2>{t("sonic_calendar.public_pick_service")}</h2>
                <div className={styles.serviceChoices}>
                  {page.services.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      className={s.id === serviceId ? styles.serviceChoiceOn : styles.serviceChoice}
                      onClick={() => {
                        setServiceId(s.id);
                        setPicked(null);
                        // The picked person may not do this service.
                        if (staffId && !(page.staff || []).some((m) => m.id === staffId && (!m.service_ids || m.service_ids.includes(s.id)))) setStaffId(null);
                      }}
                    >
                      <span>
                        <strong>{s.name}</strong>
                        {s.description ? <div className={styles.serviceMeta}>{s.description}</div> : null}
                      </span>
                      <span className={styles.serviceMeta}>{s.duration_min} min</span>
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {service && staffChoices.length ? (
              <section className={styles.publicStep} data-testid="public-staff">
                <h2>{t("sonic_calendar.public_pick_staff")}</h2>
                <div className={styles.chips}>
                  {[{ id: null, name: t("sonic_calendar.public_anyone") }, ...staffChoices].map((m) => (
                    <button
                      key={m.id || "anyone"}
                      type="button"
                      aria-pressed={staffId === m.id}
                      className={staffId === m.id ? styles.dayToggleOn : styles.dayToggle}
                      onClick={() => {
                        setStaffId(m.id);
                        setPicked(null);
                      }}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            {service ? (
              <section className={styles.publicStep}>
                <h2>
                  {t("sonic_calendar.public_pick_time")}{" "}
                  <span className={styles.serviceMeta}>({page.timezone.replace(/_/g, " ")})</span>
                </h2>
                <WeekSlots key={staffId || "anyone"} load={load} tz={page.timezone} selected={picked} onPick={setPicked} t={t} locale={locale} reloadKey={reloadKey} />
              </section>
            ) : null}

            {picked ? (
              <section className={styles.publicStep}>
                <h2>{whenLabel(picked, page.timezone, locale)}</h2>
                <div className={styles.form}>
                  <Input placeholder={t("sonic_calendar.public_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoComplete="name" />
                  <Input placeholder={t("sonic_calendar.public_phone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} autoComplete="tel" inputMode="tel" />
                  <Input
                    placeholder={page.require_email ? t("sonic_calendar.public_email_required") : t("sonic_calendar.public_email")}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    autoComplete="email"
                    inputMode="email"
                  />
                  <p className={styles.notice}>
                    {t("sonic_calendar.public_notice", { business: page.business_name })}{" "}
                    {page.privacy_url ? (
                      <a href={page.privacy_url} target="_blank" rel="noopener noreferrer">
                        {t("sonic_calendar.public_privacy")}
                      </a>
                    ) : null}
                  </p>
                  {error ? <p className={styles.error}>{error}</p> : null}
                  <Button type="primary" size="large" disabled={!canSend} loading={sending} onClick={submit}>
                    {t("sonic_calendar.public_confirm")}
                  </Button>
                </div>
              </section>
            ) : null}
          </>
        )}
        <p className={styles.footer}>{t("sonic_calendar.public_footer")}</p>
      </main>
    </div>
  );
};

export default PublicBooking;
